// The practice session's **retention** framing, as copy models (`client-practice`).
//
// **`home-and-single-player` retires the single-device half of this file.** It used to hold four more
// lines, all of them variations on "practice stays on the device it was created on". That sentence is
// no longer true and no longer wanted: the sandbox scope is being re-keyed to the authenticated
// identity, so a player's scripts do follow them, and telling them otherwise would be a false warning
// on the first screen they see. What survives is the one fact that *is* still true — the staged
// **trace bytes** expire, because device storage quota is a real bound — and it is stated exactly
// where a player would otherwise form the opposite expectation, on the expired surface itself, rather
// than as a persistent banner nobody reads (design **D-J**).
//
// The retention period is **server configuration** (**D-F**), so every function here takes it as a
// parameter. A constant of the client's own would be a second source of truth for a number the
// operator sets, and would quietly disagree the first time it was changed.
//
// Elapsed time is rendered **relatively** rather than as a formatted date, deliberately: a relative
// phrase is what the player is actually asking ("is my work still there?"), and it is locale-free, so
// the copy the tests assert is the copy every player reads.

/** Whole days between two epoch-ms instants, floored, never negative. */
export function daysBetween(fromMs: number, toMs: number): number {
  const millis = Math.max(0, toMs - fromMs);
  return Math.floor(millis / 86_400_000);
}

/** "today" · "yesterday" · "6 days ago". Used for both "last used" and "expired". */
export function daysAgoLabel(instantMs: number, nowMs: number): string {
  const days = daysBetween(instantMs, nowMs);
  if (days === 0) {
    return 'today';
  }
  if (days === 1) {
    return 'yesterday';
  }
  return `${days} days ago`;
}



/** The expired surface's line. It names **that** it expired and **when**, so an expired session can
 * never be presented as an empty new one. */
export function expiredLine(expiredAt: number, nowMs: number, retentionDays: number): string {
  return `It expired ${daysAgoLabel(expiredAt, nowMs)} — a practice colony is kept on this device for ${retentionDays} days after you last use it, and this one wasn’t opened in time.`;
}


